import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { validationMessages } from '../../messages';
import { UiSelectOptions } from '../../models/ui-select-option.model';
import { SELECT_ALL_VALUE } from '../../constants/select-all-value';

@Component({
  selector: 'app-ui-select',
  templateUrl: './ui-select.component.html',
  styleUrls: ['./ui-select.component.scss'],
  standalone: false,
})
export class SharedUiSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label = '';
  @Input() description = '';
  @Input() control: FormControl;
  @Input() options: UiSelectOptions = [];
  @Input() messages = validationMessages;
  @Input() placeholder?: string;
  @Input() selected: string[] | string | any = [];
  @Input() isMultiple = true;
  @Input() disabled = false;
  @Input() required = false;
  @Input() sortByName = false;
  @Input() enableBlankOption = false;
  @Input() returnData = false;
  @Input() returnWholeObject = false;
  @Input() enableSearch = true;
  @Input() selectAll = false;
  @Input() selectAllSingleChoice = false;
  @Input() panelClass = '';
  @Input() hasIndent = false;
  @Input() indent = 8;
  @Input() searchMode: 'local' | 'api' = 'local';
  @Input() hasMore = false;

  @Output() readonly searchChange = new EventEmitter<string>();
  @Output() readonly loadMore = new EventEmitter<void>();

  sortedOptions: UiSelectOptions = [];
  areSomeRowsSelected = false;
  areAllRowsSelected = false;
  SELECT_ALL_VALUE = SELECT_ALL_VALUE;

  @Output() readonly changeHandler: EventEmitter<MatSelectChange> =
    new EventEmitter<MatSelectChange>();

  @ViewChild('searchInput') searchInput: ElementRef;

  private initialOptions: UiSelectOptions;
  private selectedOptionForApi: NonNullable<UiSelectOptions>[number] | null = null;
  private subscription = new Subscription();
  private scrollHandler: (() => void) | null = null;
  private scrollContainer: HTMLElement | null = null;
  private lastLoadMoreEmit = 0;
  private readonly loadMoreThrottleMs = 500;

  ngOnInit(): void {
    if (!this.initialOptions && this.options) {
      this.initialOptions = this.options;
    }
    if (this.selectAll) {
      this.subscription.add(
        this.control.valueChanges.subscribe(() => this.setSelectAllCheckboxProperties())
      );
    }
    if (this.searchMode === 'api') {
      this.subscription.add(
        this.control.valueChanges.subscribe((value) => {
          if (value == null || value === '') {
            this.selectedOptionForApi = null;
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.removeScrollListener();
  }

  setSelectAllCheckboxProperties(): void {
    const opts = this.getDisplayedOptions();
    if (opts && Array.isArray(this.control.value)) {
      this.areSomeRowsSelected =
        this.control.value.length > 0 && this.control.value.length < opts.length;
      this.areAllRowsSelected = opts.length > 0 && this.control.value.length === opts.length;
    } else {
      this.areAllRowsSelected = false;
      this.areSomeRowsSelected = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options?.currentValue !== changes.options?.previousValue) {
      this.initialOptions = this.options;
      if (this.searchMode === 'api' && this.control?.value && this.options?.length) {
        const found = this.options.find((o) => o.id === this.control.value);
        if (found) {
          this.selectedOptionForApi = found;
        }
      }
      if (this.searchInput?.nativeElement && this.searchMode !== 'api') {
        this.searchInput.nativeElement.value = '';
      }
    }
    if (changes.isRequired?.currentValue === true) {
      this.control.setValidators(Validators.required);
      this.control.updateValueAndValidity();
    } else if (changes.isRequired?.currentValue === false) {
      this.control.clearValidators();
      this.control.updateValueAndValidity();
    }
    if (changes?.options?.currentValue?.length > 0) {
      this.sortedOptions = [...changes.options.currentValue].sort((a, b) =>
        a.name > b.name ? 1 : -1
      );
    }

    if (this.selectAll) {
      this.setSelectAllCheckboxProperties();
    }
  }

  onPanelOpenedChange(opened: boolean): void {
    if (opened) {
      this.focusInput();
      this.addScrollListener();
    } else {
      this.clearSearchOnClose();
      this.removeScrollListener();
    }
  }

  private clearSearchOnClose(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    if (this.searchMode === 'api') {
      this.searchChange.emit('');
    }
    this.options = this.initialOptions ?? this.options;
  }

  focusInput(): void {
    if (!this.initialOptions && this.options) {
      this.initialOptions = this.options;
    }
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.focus();
    }
    this.options = this.initialOptions ?? this.options;
  }

  private scrollPanelToTop(): void {
    const panel = document.querySelector('.mat-mdc-select-panel.app-ui-select');
    if (panel) {
      (panel as HTMLElement).scrollTop = 0;
    }
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    setTimeout(() => {
      const panel = document.querySelector('.mat-mdc-select-panel.app-ui-select');
      if (!panel || this.scrollHandler) {
        return;
      }
      this.scrollContainer = panel as HTMLElement;
      this.scrollContainer.scrollTop = 0;
      this.scrollHandler = () => this.onPanelScroll();
      this.scrollContainer.addEventListener('scroll', this.scrollHandler);
      setTimeout(() => this.scrollPanelToTop(), 50);
    });
  }

  private removeScrollListener(): void {
    if (this.scrollContainer && this.scrollHandler) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      this.scrollContainer = null;
      this.scrollHandler = null;
    }
  }

  private onPanelScroll(): void {
    if (
      this.searchMode !== 'api' ||
      !this.hasMore ||
      !this.scrollContainer ||
      !this.searchInput?.nativeElement
    ) {
      return;
    }
    const searchValue = (this.searchInput.nativeElement.value ?? '').trim();
    if (searchValue !== '') {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = this.scrollContainer;
    const threshold = 50;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      const now = Date.now();
      if (now - this.lastLoadMoreEmit < this.loadMoreThrottleMs) {
        return;
      }
      this.lastLoadMoreEmit = now;
      this.loadMore.emit();
    }
  }

  // Receive user input and send to search method**
  onSearch(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const value = target.value ?? '';
    const trimmedValue = value.trim();

    if (
      event.code === 'Space' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Space'
    ) {
      // prevent closing the dropdown on space press
      event.preventDefault();
      event.stopPropagation();
      target.value += ' ';
    }

    if (this.searchMode === 'api') {
      if (trimmedValue.length === 0) {
        this.searchChange.emit('');
        return;
      }

      if (trimmedValue.length >= 3) {
        this.searchChange.emit(trimmedValue);
      }
    } else {
      this.options = value.length > 0 ? this.search(value) : this.initialOptions;
    }
  }

  // Filter the states list and send back to populate the selectedStates**
  search(value: string) {
    const filter = value.toLowerCase();
    return this.initialOptions?.filter((option: any) => option.name.toLowerCase().includes(filter));
  }

  onChange(event: MatSelectChange): void {
    if (this.searchMode === 'api' && event.value != null) {
      const selected = this.getDisplayedOptions()?.find((o) => o.id === event.value);
      if (selected) {
        this.selectedOptionForApi = selected;
      }
      this.searchChange.emit('');
    }
    this.changeHandler.emit(event);
  }

  getDisplayedOptions(): UiSelectOptions {
    const opts = this.options ?? [];

    if (this.searchMode === 'api') {
      if (!this.selectedOptionForApi || !opts.length) {
        return opts;
      }
      const selectedId = this.control?.value;
      if (selectedId != null && opts.every((o) => o.id !== selectedId)) {
        return [this.selectedOptionForApi, ...opts];
      }
      return opts;
    }

    // Local mode: mat-select hides selection when selected options aren't in the DOM.
    // Always include selected options in the displayed list, even when they don't match the search.
    const fullOptions = this.initialOptions ?? this.options ?? [];
    const selectedValues = Array.isArray(this.control?.value) ? this.control.value : [];
    const selectedIds = selectedValues
      .map((v) => (this.returnWholeObject ? v?.id : v))
      .filter((id) => id != null && id !== this.SELECT_ALL_VALUE);

    if (selectedIds.length === 0) {
      const singleId = this.returnWholeObject ? this.control?.value?.id : this.control?.value;
      if (singleId != null && singleId !== this.SELECT_ALL_VALUE) {
        const selectedOption = fullOptions.find((o) => o.id === singleId);
        if (selectedOption && opts.every((o) => o.id !== singleId)) {
          return [selectedOption, ...opts];
        }
      }
      return opts;
    }

    const filteredIds = new Set(opts.map((o) => o.id));
    const selectedNotInFiltered = selectedIds
      .filter((id) => !filteredIds.has(id))
      .map((id) => fullOptions.find((o) => o.id === id))
      .filter(Boolean) as NonNullable<UiSelectOptions>[number][];
    if (selectedNotInFiltered.length > 0) {
      return [...selectedNotInFiltered, ...opts];
    }
    return opts;
  }

  getDisplayedOptionsForRender(): UiSelectOptions {
    const opts = this.getDisplayedOptions();
    if (!opts?.length) {
      return opts ?? [];
    }
    if (this.sortByName) {
      return [...opts].sort((a, b) => (a.name > b.name ? 1 : -1));
    }
    return opts;
  }

  compareWith(o1: any, o2: any): boolean {
    return o1?.id && o2?.id ? o1.id === o2.id : Object.is(o1, o2);
  }

  toggleSelection(change: any): void {
    if (change.checked) {
      const allOptions = this.getDisplayedOptions()?.map((option) => option.id);
      this.control.setValue(allOptions as any[]);
    } else {
      this.control.setValue([]);
    }

    this.setSelectAllCheckboxProperties();
  }

  getSelectedOptionName(value: any): string {
    if (value === null || value === undefined) return '';

    let optionId: any;
    if (this.returnWholeObject) {
      optionId = value?.id;
    } else {
      optionId = value;
    }

    if (optionId === SELECT_ALL_VALUE) {
      return 'all';
    }

    const optionsToSearch = this.getDisplayedOptions();
    const option = optionsToSearch?.find((opt) => opt.id === optionId);
    return option?.name || '';
  }

  getMultipleSelectedOptionNames(values: any[]): string {
    if (!values || !Array.isArray(values) || values.length === 0) return '';

    const selectedNames = values
      .map((value) => {
        let optionId: any;
        if (this.returnWholeObject) {
          optionId = value?.id;
        } else {
          optionId = value;
        }

        const optionsToSearch = this.getDisplayedOptions();
        const option = optionsToSearch?.find((opt) => opt.id === optionId);
        return option?.name || '';
      })
      .filter((name) => name !== '');

    return selectedNames.join(', ');
  }
}
